import { useQuery } from '@tanstack/react-query'
import { Card, Flex, Heading, Text } from '@radix-ui/themes'
import { formatDayWithYear } from '~/utils/date'
import { useTRPC } from '~/utils/trpc'
import { formatRecipients, isReturnInProgress, type MaterialReturn } from '../service'
import { InstallationIdLinks } from './InstallationIdLinks'
import { LabelExpiryText } from './LabelExpiryText'
import { MarkReturnReceivedButton } from './MarkReturnReceivedButton'
import { ReturnLabelLink } from './ReturnLabelLink'
import { ReturnStatusBadge } from './ReturnStatusBadge'
import { TrackingNumberLink } from './TrackingNumberLink'

interface InstallationMaterialReturnsProps {
  installationId: number
}

const MaterialReturnRow = ({
  materialReturn,
  installationId
}: {
  materialReturn: MaterialReturn
  installationId: number
}) => {
  const inProgress = isReturnInProgress(materialReturn)

  const otherInstallationIds = materialReturn.installations
    .map((link) => link.installation_id)
    .filter((id) => id !== installationId)

  return (
    <Card variant="surface">
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" gap="3">
          <Flex align="center" gap="2">
            <ReturnStatusBadge materialReturn={materialReturn} />
            <Text size="2" weight="medium">
              {materialReturn.material_description}
            </Text>
          </Flex>
          {inProgress ? <MarkReturnReceivedButton materialReturnId={materialReturn.id} /> : null}
        </Flex>
        <Text size="1" color="gray">
          Sent {formatDayWithYear(materialReturn.sent_at)} to {formatRecipients(materialReturn)}
          {materialReturn.received_at ? ` · Received ${formatDayWithYear(materialReturn.received_at)}` : ''}
        </Text>
        <Flex align="center" gap="2">
          <Text size="1" color="gray">
            Waybill
          </Text>
          <TrackingNumberLink trackingNumber={materialReturn.tracking_number} size="1" />
        </Flex>
        <Flex align="center" gap="2">
          <Text size="1" color="gray">
            Label expires
          </Text>
          <LabelExpiryText materialReturn={materialReturn} size="1" />
        </Flex>
        {otherInstallationIds.length > 0 ? (
          <Flex align="center" gap="2">
            <Text size="1" color="gray">
              Also covers
            </Text>
            <InstallationIdLinks installationIds={otherInstallationIds} />
          </Flex>
        ) : null}
        <ReturnLabelLink path={materialReturn.label_storage_path} />
      </Flex>
    </Card>
  )
}

export const InstallationMaterialReturns = ({ installationId }: InstallationMaterialReturnsProps) => {
  const trpc = useTRPC()
  const returnsQuery = useQuery(trpc.ops.materialReturns.listForInstallation.queryOptions({ installationId }))
  const returns = returnsQuery.data ?? []

  if (returnsQuery.isSuccess && returns.length === 0) return null

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Heading size="3">Material returns</Heading>
        {returnsQuery.isLoading ? (
          <Text size="2" color="gray">
            Loading…
          </Text>
        ) : returnsQuery.error ? (
          <Text size="2" color="red">
            {returnsQuery.error.message}
          </Text>
        ) : (
          returns.map((materialReturn) => (
            <MaterialReturnRow
              key={materialReturn.id}
              materialReturn={materialReturn}
              installationId={installationId}
            />
          ))
        )}
      </Flex>
    </Card>
  )
}
